(() => {
  'use strict';

  const RechargePurchaseSwitcher = {
    processedAttribute: 'data-rc-purchase-switcher-ready',
    selectorClass: 'rc-purchase-switcher',
    styleId: 'rc-purchase-switcher-styles',
    reopenCartStorageKey: 'rc-reopen-cart-after-switch',

    isUpdating: false,
    isScanning: false,
    productCache: new Map(),

    normalize(value) {
      return String(value || '')
        .trim()
        .toLowerCase()
        .replace(/[-_]/g, ' ')
        .replace(/\s+/g, ' ');
    },

    getShopifyRoot() {
      return window.Shopify?.routes?.root || '/';
    },

    async fetchJSON(url, options = {}) {
      const response = await fetch(url, {
        credentials: 'same-origin',
        ...options,
        headers: {
          Accept: 'application/json',
          ...(options.headers || {})
        }
      });

      if (!response.ok) {
        const responseText = await response.text();

        throw new Error(
          responseText ||
          `Request failed with status ${response.status}`
        );
      }

      return response.json();
    },

    async getCart() {
      return this.fetchJSON(
        `${this.getShopifyRoot()}cart.js`
      );
    },

    async getProduct(handle) {
      if (!handle) {
        throw new Error('Product handle is unavailable.');
      }

      if (this.productCache.has(handle)) {
        return this.productCache.get(handle);
      }

      const product = await this.fetchJSON(
        `${this.getShopifyRoot()}products/${encodeURIComponent(handle)}.js`
      );

      this.productCache.set(handle, product);

      return product;
    },

    getRechargeCartRoot() {
      const host = document.querySelector(
        '#recharge-storefront-experiences__cart-drawer'
      );

      return host?.shadowRoot || null;
    },

    getDOMSellingPlanId(domItem) {
  const nativeSelect = domItem.querySelector(
    'select[name="selling_plan"]'
  );

  return String(nativeSelect?.value || '');
},

    getRechargeCartItems(shadowRoot) {
      if (!shadowRoot) return [];

      return [
        ...shadowRoot.querySelectorAll(
          '#cart-drawer section[role="list"] > [role="listitem"]'
        )
      ];
    },

    getAllSellingPlans(product) {
      const groups = product?.selling_plan_groups || [];

      return groups.flatMap(group => {
        return (group.selling_plans || []).map(plan => ({
          ...plan,
          groupName: group.name || '',
          groupOptions: group.options || []
        }));
      });
    },

    getPlanSearchText(plan) {
      const planOptions = Array.isArray(plan?.options)
        ? plan.options.map(option => {
            if (typeof option === 'string') {
              return option;
            }

            return [
              option?.name,
              option?.value
            ].filter(Boolean).join(' ');
          })
        : [];

      const groupOptions = Array.isArray(plan?.groupOptions)
        ? plan.groupOptions.map(option => {
            if (typeof option === 'string') {
              return option;
            }

            return [
              option?.name,
              option?.value
            ].filter(Boolean).join(' ');
          })
        : [];

      return this.normalize([
        plan?.name,
        plan?.description,
        plan?.groupName,
        ...planOptions,
        ...groupOptions
      ].filter(Boolean).join(' '));
    },

    getPlanIntervalInMonths(plan) {
      const text = this.getPlanSearchText(plan);

      if (!text) return null;

      const monthMatch =
        text.match(/\bevery\s+(\d+)\s+months?\b/) ||
        text.match(/\b(\d+)\s+months?\b/) ||
        text.match(/\bmonths?\s+(\d+)\b/);

      if (monthMatch) {
        return Number(monthMatch[1]);
      }

      if (
        /\bmonthly\b/.test(text) ||
        /\bevery month\b/.test(text)
      ) {
        return 1;
      }

      if (
        /\bquarterly\b/.test(text) ||
        /\bevery quarter\b/.test(text)
      ) {
        return 3;
      }

      return null;
    },

    getVariantAllocationPlanIds(variant) {
      return (variant?.selling_plan_allocations || [])
        .map(allocation => {
          return String(
            allocation?.selling_plan_id ||
            allocation?.selling_plan?.id ||
            allocation?.id ||
            ''
          );
        })
        .filter(Boolean);
    },

    getCompatiblePlan(variant, allPlans, interval) {
      const allocationPlanIds =
        this.getVariantAllocationPlanIds(variant);

      return allPlans.find(plan => {
        const planId = String(plan.id);

        const correctInterval =
          this.getPlanIntervalInMonths(plan) === interval;

        const compatible =
          allocationPlanIds.length === 0 ||
          allocationPlanIds.includes(planId);

        return correctInterval && compatible;
      }) || null;
    },

    resolvePurchaseOptions(product) {
      const variants = product?.variants || [];
      const allPlans = this.getAllSellingPlans(product);

      let monthlyOption = null;
      let quarterlyOption = null;

      for (const variant of variants) {
        if (!monthlyOption) {
          const monthlyPlan = this.getCompatiblePlan(
            variant,
            allPlans,
            1
          );

          if (monthlyPlan) {
            monthlyOption = {
              variant,
              sellingPlan: monthlyPlan
            };
          }
        }

        if (!quarterlyOption) {
          const quarterlyPlan = this.getCompatiblePlan(
            variant,
            allPlans,
            3
          );

          if (quarterlyPlan) {
            quarterlyOption = {
              variant,
              sellingPlan: quarterlyPlan
            };
          }
        }
      }

      if (!monthlyOption || !quarterlyOption) {
        return null;
      }

      return {
        oneTime: {
          variantId: String(monthlyOption.variant.id),
          sellingPlanId: null
        },

        monthly: {
          variantId: String(monthlyOption.variant.id),
          sellingPlanId: String(
            monthlyOption.sellingPlan.id
          )
        },

        quarterly: {
          variantId: String(quarterlyOption.variant.id),
          sellingPlanId: String(
            quarterlyOption.sellingPlan.id
          )
        }
      };
    },

    getCurrentPurchaseType(cartLine, purchaseOptions) {
      const currentVariantId = String(
        cartLine?.variant_id || ''
      );

      const currentSellingPlanId = String(
        cartLine?.selling_plan_allocation
          ?.selling_plan?.id || ''
      );

      if (
        currentVariantId ===
          purchaseOptions.quarterly.variantId &&
        currentSellingPlanId ===
          purchaseOptions.quarterly.sellingPlanId
      ) {
        return 'quarterly';
      }

      if (
        currentVariantId ===
          purchaseOptions.monthly.variantId &&
        currentSellingPlanId ===
          purchaseOptions.monthly.sellingPlanId
      ) {
        return 'monthly';
      }

      return 'oneTime';
    },

    getDOMProductTitle(domItem) {
      return this.normalize(
        domItem.querySelector('p.font-semibold')
          ?.textContent
      );
    },

    getDOMVariantTitle(domItem) {
      return this.normalize(
        domItem.querySelector('dl p:first-child')
          ?.textContent
      );
    },

 findMatchingCartLine(domItem, cart, usedKeys, itemIndex) {
  const cartItems = cart?.items || [];

  const domProductTitle = this.getDOMProductTitle(domItem);
  const domVariantTitle = this.getDOMVariantTitle(domItem);
  const domSellingPlanId = this.getDOMSellingPlanId(domItem);

  /*
   * First preference:
   * Recharge ke native selling-plan selection se exact cart line.
   */
  if (domSellingPlanId) {
    const exactPlanMatch = cartItems.find(item => {
      if (usedKeys.has(item.key)) return false;

      const cartPlanId = String(
        item?.selling_plan_allocation
          ?.selling_plan?.id || ''
      );

      const cartProductTitle = this.normalize(
        item.product_title || item.title
      );

      const sameProduct =
        !domProductTitle ||
        cartProductTitle === domProductTitle ||
        this.normalize(item.title).startsWith(domProductTitle);

      return (
        sameProduct &&
        cartPlanId === domSellingPlanId
      );
    });

    if (exactPlanMatch) {
      usedKeys.add(exactPlanMatch.key);
      return exactPlanMatch;
    }
  }

  /*
   * Second preference:
   * Product title + variant title.
   */
  const titleAndVariantMatch = cartItems.find(item => {
    if (usedKeys.has(item.key)) return false;

    const cartProductTitle = this.normalize(
      item.product_title || item.title
    );

    const cartVariantTitle = this.normalize(
      item.variant_title
    );

    const sameProduct =
      !domProductTitle ||
      cartProductTitle === domProductTitle ||
      this.normalize(item.title).startsWith(domProductTitle);

    const sameVariant =
      !domVariantTitle ||
      !cartVariantTitle ||
      cartVariantTitle === domVariantTitle;

    return sameProduct && sameVariant;
  });

  if (titleAndVariantMatch) {
    usedKeys.add(titleAndVariantMatch.key);
    return titleAndVariantMatch;
  }

  /*
   * Last fallback:
   * Same index, but only when that line is still unused.
   */
  const indexedCartLine = cartItems[itemIndex];

  if (
    indexedCartLine &&
    !usedKeys.has(indexedCartLine.key)
  ) {
    usedKeys.add(indexedCartLine.key);
    return indexedCartLine;
  }

  const unusedLine = cartItems.find(
    item => !usedKeys.has(item.key)
  );

  if (unusedLine) {
    usedKeys.add(unusedLine.key);
    return unusedLine;
  }

  return null;
},

    getExistingRechargePlanContainer(domItem) {
      const nativeSelect = domItem.querySelector(
        'select[name="selling_plan"]'
      );

      if (!nativeSelect) return null;

      let node = nativeSelect.parentElement;

      while (node && node !== domItem) {
        const classes = node.classList;

        if (
          classes?.contains('flex') &&
          classes?.contains('flex-col') &&
          classes?.contains('items-start') &&
          classes?.contains('w-full')
        ) {
          return node;
        }

        node = node.parentElement;
      }

      return nativeSelect.parentElement;
    },

    createOption(value, label, selectedValue) {
      const option = document.createElement('option');

      option.value = value;
      option.textContent = label;
      option.selected = value === selectedValue;

      return option;
    },

    formatMoney(cents) {
      return `$${(Number(cents || 0) / 100).toFixed(2)}`;
    },

    getVariantById(product, variantId) {
      return (product?.variants || []).find(
        variant => String(variant.id) === String(variantId)
      );
    },

    getSellingPlanAllocation(variant, sellingPlanId) {
  if (!variant || !sellingPlanId) return null;

  return (
    variant.selling_plan_allocations || []
  ).find(allocation => {
    const allocationPlanId = String(
      allocation?.selling_plan_id ||
      allocation?.selling_plan?.id ||
      allocation?.id ||
      ''
    );

    return allocationPlanId === String(sellingPlanId);
  }) || null;
},

getAllocationPrice(allocation) {
  if (!allocation) return null;

  /*
   * Shopify product.js normally returns price in cents.
   */
  if (
    allocation.price !== undefined &&
    allocation.price !== null
  ) {
    if (typeof allocation.price === 'number') {
      return allocation.price;
    }

    if (
      typeof allocation.price === 'object' &&
      allocation.price.amount !== undefined
    ) {
      return Math.round(
        Number(allocation.price.amount) * 100
      );
    }
  }

  if (
    allocation.per_delivery_price !== undefined &&
    allocation.per_delivery_price !== null
  ) {
    if (
      typeof allocation.per_delivery_price === 'number'
    ) {
      return allocation.per_delivery_price;
    }

    if (
      typeof allocation.per_delivery_price === 'object' &&
      allocation.per_delivery_price.amount !== undefined
    ) {
      return Math.round(
        Number(
          allocation.per_delivery_price.amount
        ) * 100
      );
    }
  }

  return null;
},

   getOptionLabel(type, product, purchaseOptions) {
  if (type === 'oneTime') {
    const variant = this.getVariantById(
      product,
      purchaseOptions.oneTime.variantId
    );

    return variant?.price !== undefined
      ? `One-time purchase — ${this.formatMoney(
          variant.price
        )}`
      : 'One-time purchase';
  }

  if (type === 'monthly') {
    const monthlyVariant = this.getVariantById(
      product,
      purchaseOptions.monthly.variantId
    );

    const monthlyAllocation =
      this.getSellingPlanAllocation(
        monthlyVariant,
        purchaseOptions.monthly.sellingPlanId
      );

    const monthlyPrice =
      this.getAllocationPrice(monthlyAllocation);

    return monthlyPrice !== null
      ? `Monthly subscription — ${this.formatMoney(
          monthlyPrice
        )}`
      : 'Monthly subscription';
  }

  const quarterlyVariant = this.getVariantById(
    product,
    purchaseOptions.quarterly.variantId
  );

  const quarterlyAllocation =
    this.getSellingPlanAllocation(
      quarterlyVariant,
      purchaseOptions.quarterly.sellingPlanId
    );

  const quarterlyPrice =
    this.getAllocationPrice(quarterlyAllocation) ??
    quarterlyVariant?.price;

  return quarterlyPrice !== undefined &&
    quarterlyPrice !== null
    ? `3-Month Program — ${this.formatMoney(
        quarterlyPrice
      )}`
    : '3-Month Program';
},

getThreeMonthSavingsData(product, purchaseOptions) {
  const standardVariant = this.getVariantById(
    product,
    purchaseOptions.oneTime.variantId
  );

  const quarterlyVariant = this.getVariantById(
    product,
    purchaseOptions.quarterly.variantId
  );

  if (!standardVariant || !quarterlyVariant) {
    return null;
  }

  const quarterlyAllocation =
    this.getSellingPlanAllocation(
      quarterlyVariant,
      purchaseOptions.quarterly.sellingPlanId
    );

  const oneTimePrice = Number(
    standardVariant.price
  );

  const allocationPrice =
    this.getAllocationPrice(
      quarterlyAllocation
    );

  const quarterlyPrice = Number(
    allocationPrice !== null
      ? allocationPrice
      : quarterlyVariant.price
  );

  if (
    !Number.isFinite(oneTimePrice) ||
    !Number.isFinite(quarterlyPrice)
  ) {
    return null;
  }

  const equivalentOneTimeTotal =
    oneTimePrice * 3;

  const savingAmount = Math.max(
    equivalentOneTimeTotal - quarterlyPrice,
    0
  );

  const savingPercentage =
    equivalentOneTimeTotal > 0
      ? (
          savingAmount /
          equivalentOneTimeTotal
        ) * 100
      : 0;

  return {
    equivalentOneTimeTotal,
    quarterlyPrice,
    savingAmount,
    savingPercentage,
    monthlyEquivalent: quarterlyPrice / 3
  };
},

formatPercentage(value) {
  const rounded = Math.round(
    Number(value || 0) * 100
  ) / 100;

  return Number.isInteger(rounded)
    ? `${rounded}%`
    : `${rounded.toFixed(2)}%`;
},

updateCartItemPriceDisplay(
  domItem,
  currentType,
  product,
  purchaseOptions
) {
  /*
   * Recharge already renders the correct standard display
   * for One-time and Monthly. Only modify quarterly.
   */
  if (currentType !== 'quarterly') {
    return;
  }

  const savings =
    this.getThreeMonthSavingsData(
      product,
      purchaseOptions
    );

  if (!savings) return;

  const existingMoney = domItem.querySelector(
    'span[part~="money"]'
  );

  if (!existingMoney) return;

  const priceContainer = existingMoney.closest(
    '.text-right.whitespace-nowrap'
  );

  if (!priceContainer) return;

  if (
    priceContainer.hasAttribute(
      'data-rc-quarterly-price-ready'
    )
  ) {
    return;
  }

  priceContainer.replaceChildren();

  const originalPriceAccessible =
    document.createElement('span');

  originalPriceAccessible.className = 'sr-only';
  originalPriceAccessible.textContent =
    `Original one-time equivalent: ${this.formatMoney(
      savings.equivalentOneTimeTotal
    )}`;

  const originalPrice =
    document.createElement('span');

  originalPrice.className =
    'text-storefront-drawer-muted line-through mr-2';

  originalPrice.setAttribute('part', 'money');

  originalPrice.textContent = this.formatMoney(
    savings.equivalentOneTimeTotal
  );

  const currentPriceAccessible =
    document.createElement('span');

  currentPriceAccessible.className = 'sr-only';
  currentPriceAccessible.textContent =
    `3-Month Program price: ${this.formatMoney(
      savings.quarterlyPrice
    )}`;

  const currentPrice =
    document.createElement('span');

  currentPrice.className =
    'text-storefront-drawer-accent font-semibold';

  currentPrice.setAttribute(
    'part',
    'money discount'
  );

  currentPrice.textContent = this.formatMoney(
    savings.quarterlyPrice
  );

  const savingLine =
    document.createElement('div');

  savingLine.className =
    'text-xs text-right text-storefront-drawer-accent font-semibold';

  savingLine.textContent =
    `Saving ${this.formatMoney(
      savings.savingAmount
    )} (${this.formatPercentage(
      savings.savingPercentage
    )})`;

  const monthlyLine =
    document.createElement('div');

  monthlyLine.className =
    'text-xs text-right text-storefront-drawer-muted';

  monthlyLine.textContent =
    `${this.formatMoney(
      savings.monthlyEquivalent
    )}/month`;

  priceContainer.append(
    originalPriceAccessible,
    originalPrice,
    currentPriceAccessible,
    currentPrice,
    savingLine,
    monthlyLine
  );

  priceContainer.setAttribute(
    'data-rc-quarterly-price-ready',
    'true'
  );
},

 updateNote(
  note,
  type,
  product,
  purchaseOptions
) {
  if (type === 'quarterly') {
    const savings =
      this.getThreeMonthSavingsData(
        product,
        purchaseOptions
      );

    note.textContent = savings
      ? `${this.formatMoney(
          savings.monthlyEquivalent
        )}/month, billed and delivered as ${this.formatMoney(
          savings.quarterlyPrice
        )} every 3 months`
      : 'Billed and delivered every 3 months';

    return;
  }

  if (type === 'monthly') {
    note.textContent =
      'Billed and delivered monthly';
    return;
  }

  note.textContent = 'One-time purchase';
},

    saveReopenCartState() {
      try {
        sessionStorage.setItem(
          this.reopenCartStorageKey,
          'true'
        );
      } catch (error) {
        console.warn(
          '[Recharge switcher] Could not save cart state',
          error
        );
      }
    },

    shouldReopenCart() {
      try {
        return sessionStorage.getItem(
          this.reopenCartStorageKey
        ) === 'true';
      } catch (error) {
        return false;
      }
    },

    clearReopenCartState() {
      try {
        sessionStorage.removeItem(
          this.reopenCartStorageKey
        );
      } catch (error) {
        console.warn(
          '[Recharge switcher] Could not clear cart state',
          error
        );
      }
    },

    isRechargeCartOpen() {
      const shadowRoot = this.getRechargeCartRoot();

      if (!shadowRoot) return false;

      const cartSheet = shadowRoot.querySelector(
        '#CartSheet'
      );

      return (
        cartSheet &&
        cartSheet.getAttribute('data-state') === 'open'
      );
    },

    isVisibleElement(element) {
      if (!element) return false;

      const style = window.getComputedStyle(element);
      const rect = element.getBoundingClientRect();

      return (
        style.display !== 'none' &&
        style.visibility !== 'hidden' &&
        Number(style.opacity) !== 0 &&
        rect.width > 0 &&
        rect.height > 0
      );
    },

    findCartOpenTrigger() {
      const selectors = [
        '[data-recharge-cart-trigger]',
        '[data-cart-drawer-trigger]',
        '[data-cart-trigger]',
        '[data-cart-icon-bubble]',
        '#cart-icon-bubble',
        'button[aria-label*="shopping cart" i]',
        'button[aria-label*="open cart" i]',
        'button[aria-label*="cart" i]',
        '[role="button"][aria-label*="cart" i]',
        'a[aria-label*="shopping cart" i]',
        'a[aria-label*="open cart" i]',
        'a[aria-label*="cart" i]',
        'a[href="/cart"]'
      ];

      for (const selector of selectors) {
        const elements = [
          ...document.querySelectorAll(selector)
        ];

        const trigger = elements.find(element => {
          if (!this.isVisibleElement(element)) {
            return false;
          }

          const text = this.normalize([
            element.textContent,
            element.getAttribute('aria-label'),
            element.getAttribute('title')
          ].filter(Boolean).join(' '));

          return (
            !text.includes('checkout') &&
            !text.includes('remove') &&
            !text.includes('delete')
          );
        });

        if (trigger) return trigger;
      }

      return null;
    },

    reopenRechargeCartAfterReload() {
      if (!this.shouldReopenCart()) return;

      let attempt = 0;
      const maximumAttempts = 15;

      const attemptOpen = () => {
        attempt += 1;

        if (this.isRechargeCartOpen()) {
          this.clearReopenCartState();

          window.setTimeout(
            () => this.scan(),
            300
          );

          return;
        }

        const trigger = this.findCartOpenTrigger();

        if (trigger) {
          trigger.dispatchEvent(
            new MouseEvent('click', {
              view: window,
              bubbles: true,
              cancelable: true
            })
          );
        }

        window.setTimeout(() => {
          if (this.isRechargeCartOpen()) {
            this.clearReopenCartState();

            window.setTimeout(
              () => this.scan(),
              300
            );

            return;
          }

          if (attempt < maximumAttempts) {
            window.setTimeout(
              attemptOpen,
              400
            );
          } else {
            this.clearReopenCartState();

            console.warn(
              '[Recharge switcher] Cart updated, but it could not be reopened automatically.'
            );
          }
        }, 350);
      };

      window.setTimeout(
        attemptOpen,
        500
      );
    },

    async replaceCartLine(cartLine, targetOption) {
      if (this.isUpdating) return;

      this.isUpdating = true;

      try {
        if (
          !cartLine?.key ||
          !targetOption?.variantId
        ) {
          throw new Error(
            'Cart line or target variant is unavailable.'
          );
        }

        const originalProperties =
          cartLine.properties || {};

        const originalQuantity =
          Math.max(Number(cartLine.quantity) || 1, 1);

        const originalSellingPlanId =
          cartLine.selling_plan_allocation
            ?.selling_plan?.id || null;

        await this.fetchJSON(
          `${this.getShopifyRoot()}cart/change.js`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              id: cartLine.key,
              quantity: 0
            })
          }
        );

        const replacementItem = {
          id: Number(targetOption.variantId),
          quantity: 1,
          properties: originalProperties
        };

        if (targetOption.sellingPlanId) {
          replacementItem.selling_plan = Number(
            targetOption.sellingPlanId
          );
        }

        try {
          await this.fetchJSON(
            `${this.getShopifyRoot()}cart/add.js`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                items: [replacementItem]
              })
            }
          );
        } catch (addError) {
          const restoreItem = {
            id: Number(cartLine.variant_id),
            quantity: originalQuantity,
            properties: originalProperties
          };

          if (originalSellingPlanId) {
            restoreItem.selling_plan = Number(
              originalSellingPlanId
            );
          }

          await this.fetchJSON(
            `${this.getShopifyRoot()}cart/add.js`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                items: [restoreItem]
              })
            }
          );

          throw addError;
        }

        document.documentElement.dispatchEvent(
          new CustomEvent('cart:refresh', {
            bubbles: true
          })
        );

        document.dispatchEvent(
          new CustomEvent('cart:updated', {
            bubbles: true
          })
        );

        window.dispatchEvent(
          new CustomEvent('cart:update', {
            bubbles: true
          })
        );

        this.saveReopenCartState();

        window.location.reload();
      } catch (error) {
        console.error(
          '[Recharge purchase switcher update]',
          error
        );

        window.alert(
          'We could not update your purchase option. ' +
          'Your previous selection has been restored. Please try again.'
        );

        window.location.reload();
      } finally {
        this.isUpdating = false;
      }
    },

    async injectForCartItem(
      domItem,
      cartLine,
      product
    ) {
      if (
        !domItem ||
        !cartLine ||
        domItem.hasAttribute(this.processedAttribute)
      ) {
        return;
      }

      const purchaseOptions =
        this.resolvePurchaseOptions(product);

      if (!purchaseOptions) return;

      const currentType =
        this.getCurrentPurchaseType(
          cartLine,
          purchaseOptions
        );
        this.updateCartItemPriceDisplay(
  domItem,
  currentType,
  product,
  purchaseOptions
);

      const existingPlanContainer =
        this.getExistingRechargePlanContainer(
          domItem
        );

      const wrapper = document.createElement('div');

      wrapper.className = [
        this.selectorClass,
        'flex',
        'gap-2',
        'flex-col',
        'items-start',
        'w-full'
      ].join(' ');

      const inner = document.createElement('div');
      inner.className =
        'flex flex-col space-y-1 w-full';

      const label = document.createElement('label');
      label.className =
        `${this.selectorClass}__label text-sm`;
      label.textContent = 'Purchase option';

      const selectWrapper =
        document.createElement('div');

      selectWrapper.className = 'relative w-full';

      const select = document.createElement('select');

      select.className = [
        `${this.selectorClass}__select`,
        'border',
        'border-storefront-drawer-border',
        'rounded-storefront-drawer',
        'pl-3',
        'py-2',
        'pr-10',
        'w-full',
        'ring-storefront-drawer-primary/80',
        'focus-visible:ring-2',
        'focus-visible:outline-none',
        'focus-visible:ring-offset-2',
        'appearance-none',
        'text-sm'
      ].join(' ');

      select.setAttribute(
        'aria-label',
        'Purchase option'
      );

      select.append(
        this.createOption(
          'oneTime',
          this.getOptionLabel(
            'oneTime',
            product,
            purchaseOptions
          ),
          currentType
        ),

        this.createOption(
          'monthly',
          this.getOptionLabel(
            'monthly',
            product,
            purchaseOptions
          ),
          currentType
        ),

        this.createOption(
          'quarterly',
          this.getOptionLabel(
            'quarterly',
            product,
            purchaseOptions
          ),
          currentType
        )
      );

      const arrow = document.createElement('span');
      arrow.className =
        `${this.selectorClass}__arrow`;
      arrow.setAttribute('aria-hidden', 'true');
      arrow.textContent = '\u23F7';

      const note = document.createElement('p');

      note.className = [
        `${this.selectorClass}__note`,
        'text-sm',
        'text-storefront-drawer-muted'
      ].join(' ');

   this.updateNote(
  note,
  currentType,
  product,
  purchaseOptions
);

      select.addEventListener(
        'change',
        async event => {
          const nextType = event.target.value;

          if (nextType === currentType) return;

          const targetOption =
            purchaseOptions[nextType];

          if (
            !targetOption ||
            !targetOption.variantId ||
            (
              nextType !== 'oneTime' &&
              !targetOption.sellingPlanId
            )
          ) {
            event.target.value = currentType;

            window.alert(
              'This purchase option is currently unavailable.'
            );

            return;
          }

          select.disabled = true;
          note.textContent =
            'Updating your purchase option…';

          await this.replaceCartLine(
            cartLine,
            targetOption
          );
        }
      );

      selectWrapper.append(select, arrow);
      inner.append(label, selectWrapper, note);
      wrapper.appendChild(inner);

      if (existingPlanContainer) {
        existingPlanContainer.before(wrapper);
        existingPlanContainer.style.display = 'none';
        existingPlanContainer.setAttribute(
          'aria-hidden',
          'true'
        );
      } else {
        domItem.appendChild(wrapper);
      }

      domItem.setAttribute(
        this.processedAttribute,
        'true'
      );
    },

    addStyles(shadowRoot) {
      if (!shadowRoot) return;

      if (
        shadowRoot.querySelector(`#${this.styleId}`)
      ) {
        return;
      }

      const style = document.createElement('style');
      style.id = this.styleId;

      style.textContent = `
        .${this.selectorClass} {
          margin-top: 4px;
          width: 100%;
        }

        .${this.selectorClass}__label {
          display: block;
          margin-bottom: 4px;
          font-size: 14px;
          font-weight: 400;
        }

        .${this.selectorClass}__select {
          display: block;
          width: 100%;
          min-height: 42px;
          background: transparent;
          color: inherit;
          cursor: pointer;
        }

        .${this.selectorClass}__select:disabled {
          cursor: wait;
          opacity: 0.65;
        }

        .${this.selectorClass}__arrow {
          position: absolute;
          right: 12px;
          top: 50%;
          transform: translateY(-50%);
          pointer-events: none;
          font-size: 16px;
          line-height: 1;
        }

        .${this.selectorClass}__note {
          margin: 5px 0 0;
          font-size: 12px;
          line-height: 1.4;
        }
      `;

      shadowRoot.appendChild(style);
    },

    async scan() {
      if (this.isUpdating || this.isScanning) {
        return;
      }

      this.isScanning = true;

      try {
        const shadowRoot =
          this.getRechargeCartRoot();

        if (!shadowRoot) return;

        this.addStyles(shadowRoot);

        const domItems =
          this.getRechargeCartItems(shadowRoot);

        if (!domItems.length) return;

        const cart = await this.getCart();
        const usedCartLineKeys = new Set();

        for (
          let index = 0;
          index < domItems.length;
          index += 1
        ) {
          const domItem = domItems[index];

          const cartLine =
            this.findMatchingCartLine(
              domItem,
              cart,
              usedCartLineKeys,
              index
            );

          if (
            !cartLine ||
            !cartLine.handle ||
            domItem.hasAttribute(
              this.processedAttribute
            )
          ) {
            continue;
          }

          try {
            const product = await this.getProduct(
              cartLine.handle
            );

            await this.injectForCartItem(
              domItem,
              cartLine,
              product
            );
          } catch (productError) {
            console.warn(
              `[Recharge switcher] Could not process ${cartLine.handle}`,
              productError
            );
          }
        }
      } catch (error) {
        console.error(
          '[Purchase option switcher scan]',
          error
        );
      } finally {
        this.isScanning = false;
      }
    },

    init() {
      this.scan();
      this.reopenRechargeCartAfterReload();

      window.setInterval(() => {
        this.scan();
      }, 750);

      document.addEventListener(
        'click',
        () => {
          window.setTimeout(
            () => this.scan(),
            200
          );

          window.setTimeout(
            () => this.scan(),
            600
          );

          window.setTimeout(
            () => this.scan(),
            1200
          );
        },
        true
      );

      document.addEventListener(
        'cart:updated',
        () => {
          window.setTimeout(
            () => this.scan(),
            300
          );
        }
      );

      window.addEventListener(
        'pageshow',
        () => {
          window.setTimeout(
            () => this.scan(),
            300
          );

          window.setTimeout(
            () => this.reopenRechargeCartAfterReload(),
            500
          );
        }
      );
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => RechargePurchaseSwitcher.init()
    );
  } else {
    RechargePurchaseSwitcher.init();
  }
})();